// SPDX-License-Identifier: AGPL-3.0-or-later

import {getUserAccentColor} from '@app/features/theme/utils/AccentColorUtils';
import {Scroller} from '@app/features/ui/components/Scroller';
import userProfileModalStyles from '@app/features/user/components/modals/UserProfileModal.module.css';
import {ProfileBody} from '@app/features/user/components/modals/user_profile_modal/ProfileBody';
import {ProfileContent} from '@app/features/user/components/modals/user_profile_modal/ProfileContent';
import {ProfileMediaHeader} from '@app/features/user/components/modals/user_profile_modal/ProfileMediaHeader';
import type {ProfileModalContentProps} from '@app/features/user/components/modals/user_profile_modal/UserProfileModalShared';
import {UserInfo} from '@app/features/user/components/modals/user_profile_modal/UserProfileModalUserInfo';
import {ProfileEffectOverlay} from '@app/features/user/components/ProfileEffectOverlay';
import * as ProfileDisplayUtils from '@app/features/user/utils/ProfileDisplayUtils';
import {resolveProfileGuildMembership, toProfileDisplayContext} from '@app/features/user/utils/ProfileGuildMembership';
import {
	MEDIA_PROXY_AVATAR_SIZE_PROFILE,
	MEDIA_PROXY_PROFILE_BANNER_SIZE_MODAL,
} from '@fluxer/constants/src/MediaProxyAssetSizes';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const renderNoActionButtons = () => null;

export const ProfileModalContent: React.FC<ProfileModalContentProps> = observer(
	({
		profile,
		user,
		userNote,
		autoFocusNote,
		noteRef,
		renderActionButtons,
		previewOverrides,
		showProfileDataWarning,
	}) => {
		const effectiveProfile = profile?.getEffectiveProfile() ?? null;
		const bannerColor = getUserAccentColor(user, effectiveProfile?.accent_color);
		const membership = resolveProfileGuildMembership(profile);
		const profileContext = useMemo<ProfileDisplayUtils.ProfileDisplayContext>(
			() =>
				toProfileDisplayContext({
					user,
					profile,
					membership,
					guildMemberProfile: profile?.guildMemberProfile,
				}),
			[user, profile, membership],
		);
		const {avatarUrl, hoverAvatarUrl} = useMemo(
			() => ProfileDisplayUtils.getProfileAvatarUrls(profileContext, previewOverrides, MEDIA_PROXY_AVATAR_SIZE_PROFILE),
			[profileContext, previewOverrides],
		);
		const {bannerUrl, hoverBannerUrl} = useMemo(
			() =>
				ProfileDisplayUtils.getProfileBannerUrls(
					profileContext,
					previewOverrides,
					MEDIA_PROXY_PROFILE_BANNER_SIZE_MODAL,
				),
			[profileContext, previewOverrides],
		);
		// Discord-style layout: profile card on the left, activity / mutuals tabs on the right.
		return (
			<div className={userProfileModalStyles.profileLayout} data-flx="user.user-profile-modal.profile-layout">
				<div className={userProfileModalStyles.profileCard} data-flx="user.user-profile-modal.profile-card">
					<ProfileEffectOverlay userId={user.id} />
					<Scroller className={userProfileModalStyles.profileCardScroller} key="user-profile-modal-card-scroller">
						<ProfileMediaHeader
							user={user}
							profile={profile}
							profileContext={profileContext}
							previewOverrides={previewOverrides}
							bannerColor={bannerColor}
							bannerUrl={bannerUrl}
							hoverBannerUrl={hoverBannerUrl}
							avatarUrl={avatarUrl}
							hoverAvatarUrl={hoverAvatarUrl}
							renderActionButtons={renderNoActionButtons}
							data-flx="user.user-profile-modal.profile-modal-content.profile-media-header"
						/>
						<div className={userProfileModalStyles.profileCardBody}>
							<UserInfo
								user={user}
								profile={profile}
								guildId={profile.guildId ?? undefined}
								showProfileDataWarning={showProfileDataWarning}
								data-flx="user.user-profile-modal.profile-card.user-info"
							/>
							<div className={userProfileModalStyles.profileCardActions}>{renderActionButtons()}</div>
							<ProfileContent
								section="about"
								profile={profile}
								user={user}
								userNote={userNote}
								autoFocusNote={autoFocusNote}
								noteRef={noteRef}
								data-flx="user.user-profile-modal.profile-card.profile-content"
							/>
						</div>
					</Scroller>
				</div>
				<ProfileBody
					profile={profile}
					user={user}
					userNote={userNote}
					autoFocusNote={autoFocusNote}
					noteRef={noteRef}
					data-flx="user.user-profile-modal.profile-modal-content.profile-body"
				/>
			</div>
		);
	},
);
